import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, FormControlLabel, LinearProgress, Paper, Radio, RadioGroup, Tooltip, Typography,
} from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
// types theme.vars, which the console's theme (cssVariables) sets
import type {} from '@mui/material/themeCssVarsAugmentation';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import axios from 'axios';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { API_PATH } from '../../../App';
import TableLoader from '../GlobalComponents/TableLoader';

// Rewards (income F2Pool credits each day, not a payment) and payouts (on-chain payments, with a txid) of the
// F2Pool accounts in segments-pool's f2pool.payments.history.ids, via cloud-mining's /segPoolRewardDistribution
// pass-through. Every load refetches the account from F2Pool; segments-pool saves and returns the last 14 days.

interface PoolAccount {
    accountName: string;
    wallet: string;
}

interface PoolEntry {
    id: string;
    // a reward's earned day, a payout's day sent (UTC), as yyyy-MM-dd
    date: string;
    createdAt: string;
    type: 'REWARD' | 'PAYOUT';
    amount: number;
    currencyCode: string;
    hashRate: string | null;
    txid: string | null;
    address: string | null;
}

interface PoolHistory {
    accountName: string;
    wallet: string;
    fetchedAt: string | null;
    fetchError: string | null;
    entries: PoolEntry[];
}

interface DateRow {
    date: string;
    rewards: PoolEntry[];
    payouts: PoolEntry[];
}

// segments-pool's F2PoolPaymentsHistoryService.HISTORY_DAYS: it returns dates from today back this many days
const HISTORY_DAYS = 14;

// a function, not a constant: App imports this module (via MainView), so API_PATH is not initialized
// yet while this module is being evaluated
const base = (): string => `${API_PATH}/segPoolRewardDistribution/pool-payments`;

const coinAmount = (value: number): string => Number(value).toFixed(8);

const dateTimeOf = (value: string | null): string => (value == null ? '' : new Date(value).toLocaleString());

const errorMessageOf = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data).message || fallback;
        } catch {
            return data || fallback;
        }
    }
    return data?.message || fallback;
};

// today (UTC) and the HISTORY_DAYS days before it, newest first
const windowDates = (): string[] => {
    const now = new Date();
    return Array.from({ length: HISTORY_DAYS + 1 }, (_, i) =>
        new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i)).toISOString().slice(0, 10));
};

const datePart = (date: string, options: Intl.DateTimeFormatOptions): string =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { ...options, timeZone: 'UTC' });

type Tone = 'success' | 'info' | 'error';

// the console's theme renders colors as CSS variables per light/dark scheme, while theme.palette only has the
// light values; fall back to the palette when the theme has no variables
const toneOf = (theme: Theme, tone: Tone) => ({
    solid: theme.vars ? theme.vars.palette[tone].main : theme.palette[tone].main,
    tint: (opacity: number): string => theme.vars
        ? `rgba(${theme.vars.palette[tone].mainChannel} / ${opacity})`
        : alpha(theme.palette[tone].main, opacity),
});

const isMismatch = (entry: PoolEntry, wallet: string): boolean =>
    entry.type === 'PAYOUT' && (entry.address ?? '').trim() !== wallet.trim();

const Mono = ({ children }: { children: React.ReactNode }) => (
    <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>{children}</Box>
);

function EntryCard({ entry, wallet }: { entry: PoolEntry; wallet: string }) {
    const theme = useTheme();
    const isPayout = entry.type === 'PAYOUT';
    const mismatch = isMismatch(entry, wallet);
    const tone = toneOf(theme, mismatch ? 'error' : isPayout ? 'info' : 'success');
    const color = tone.solid;

    const tooltip = isPayout ? (
        <Box sx={{ py: 0.5 }}>
            <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>Transaction ID</Typography>
            <Mono>{entry.txid ?? 'none yet'}</Mono>
            {mismatch && (
                <>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 1 }}>Paid to</Typography>
                    <Mono>{entry.address ?? '-'}</Mono>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 1 }}>Configured wallet</Typography>
                    <Mono>{wallet}</Mono>
                </>
            )}
        </Box>
    ) : (
        <Box sx={{ py: 0.5 }}>
            <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>Hash rate</Typography>
            <Mono>{entry.hashRate ?? '-'}</Mono>
        </Box>
    );

    return (
        <Tooltip title={tooltip} arrow placement={isPayout ? 'right' : 'left'}>
            <Paper
                variant="outlined"
                tabIndex={0}
                sx={{
                    width: '100%',
                    maxWidth: { md: 280 },
                    px: 1.5,
                    py: 1,
                    cursor: 'default',
                    bgcolor: mismatch ? tone.tint(0.12) : 'background.paper',
                    borderColor: mismatch ? color : 'divider',
                    borderLeft: `4px solid ${color}`,
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                    '&:hover, &:focus-visible': { transform: 'translateY(-1px)', boxShadow: `0 2px 10px ${tone.tint(0.35)}` },
                    '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="overline" sx={{ color, fontWeight: 700, lineHeight: 1.6 }}>{entry.type}</Typography>
                    <Typography variant="caption" color="text.secondary">{entry.date}</Typography>
                </Box>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {coinAmount(entry.amount)}{' '}
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{entry.currencyCode?.toUpperCase()}</Box>
                </Typography>
                {mismatch && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">Paid to an address other than the configured wallet</Typography>
                    </Box>
                )}
            </Paper>
        </Tooltip>
    );
}

function DateNode({ date, active }: { date: string; active: boolean }) {
    return (
        <Box
            sx={{
                zIndex: 1,
                mt: 1,
                width: 64,
                py: 0.5,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
                opacity: active ? 1 : 0.55,
                alignSelf: 'flex-start',
            }}
        >
            <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {datePart(date, { weekday: 'short' })}
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>{datePart(date, { day: 'numeric' })}</Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {datePart(date, { month: 'short' })}
            </Typography>
        </Box>
    );
}

function LegendItem({ color, label }: { color: string; label: string }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
    );
}

export default function PoolPayments() {
    const theme = useTheme();
    const [accounts, setAccounts] = useState<PoolAccount[] | null>(null);
    const [selected, setSelected] = useState('');
    const [history, setHistory] = useState<PoolHistory | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // only the latest request may update the page, so a slow response for another account cannot replace it
    const latestRequest = useRef(0);

    useEffect(() => {
        axios.get<PoolAccount[]>(`${base()}/accounts`, getAxiosConfig())
            .then(response => {
                setAccounts(response.data);
                if (response.data.length > 0) {
                    setSelected(response.data[0].accountName);
                }
            })
            .catch(err => {
                setAccounts([]);
                setError(errorMessageOf(err, 'Failed to load the F2Pool accounts.'));
            });
    }, []);

    const fetchHistory = useCallback((accountName: string) => {
        const request = ++latestRequest.current;
        setLoading(true);
        setError(null);
        axios.get<PoolHistory>(`${base()}/${encodeURIComponent(accountName)}`, getAxiosConfig())
            .then(response => {
                if (request === latestRequest.current) {
                    setHistory(response.data);
                }
            })
            .catch(err => {
                if (request === latestRequest.current) {
                    setError(errorMessageOf(err, `Failed to load pool payments for ${accountName}.`));
                }
            })
            .finally(() => {
                if (request === latestRequest.current) {
                    setLoading(false);
                }
            });
    }, []);

    useEffect(() => {
        if (selected) {
            fetchHistory(selected);
        }
    }, [selected, fetchHistory]);

    // the history of another account is not shown while the selected one loads
    const shown = history?.accountName === selected ? history : null;
    const wallet = shown?.wallet ?? accounts?.find(a => a.accountName === selected)?.wallet ?? '';

    const rows: DateRow[] = useMemo(() => {
        if (!shown) {
            return [];
        }
        const byDate = new Map<string, DateRow>(windowDates().map(date => [date, { date, rewards: [], payouts: [] }]));
        shown.entries.forEach(entry => {
            const date = entry.date.slice(0, 10);
            if (!byDate.has(date)) {
                byDate.set(date, { date, rewards: [], payouts: [] });
            }
            const row = byDate.get(date)!;
            (entry.type === 'PAYOUT' ? row.payouts : row.rewards).push(entry);
        });
        return Array.from(byDate.values()).sort((a, b) => b.date.localeCompare(a.date));
    }, [shown]);

    // per currency, in case an account ever reports more than one
    const totals = useMemo(() => {
        const byCurrency = new Map<string, { rewards: number; payouts: number }>();
        (shown?.entries ?? []).forEach(entry => {
            const currency = (entry.currencyCode ?? '').toUpperCase();
            const total = byCurrency.get(currency) ?? { rewards: 0, payouts: 0 };
            if (entry.type === 'PAYOUT') {
                total.payouts += Number(entry.amount);
            } else {
                total.rewards += Number(entry.amount);
            }
            byCurrency.set(currency, total);
        });
        return Array.from(byCurrency.entries());
    }, [shown]);

    const mismatches = shown ? shown.entries.filter(entry => isMismatch(entry, shown.wallet)).length : 0;

    if (accounts === null) {
        return <TableLoader />;
    }
    if (accounts.length === 0) {
        return error
            ? <Alert severity="error">{error}</Alert>
            : <Alert severity="info">No F2Pool accounts are configured (segments-pool's f2pool.payments.history.ids).</Alert>;
    }

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">F2Pool account</Typography>
                        <RadioGroup row value={selected} onChange={event => setSelected(event.target.value)}>
                            {accounts.map(account => (
                                <FormControlLabel
                                    key={account.accountName}
                                    value={account.accountName}
                                    control={<Radio size="small" />}
                                    label={account.accountName}
                                />
                            ))}
                        </RadioGroup>
                    </Box>
                    <Box sx={{ minWidth: 0, flex: '1 1 320px', textAlign: { xs: 'left', md: 'right' } }}>
                        <Typography variant="caption" color="text.secondary" display="block">Configured payout wallet</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{wallet}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {shown
                                ? shown.fetchedAt ? `Fetched from F2Pool ${dateTimeOf(shown.fetchedAt)}` : 'Never fetched from F2Pool'
                                : ' '}
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        disabled={loading || !selected}
                        onClick={() => fetchHistory(selected)}
                    >
                        Refresh
                    </Button>
                </Box>
                {loading && <LinearProgress sx={{ mt: 1.5 }} />}
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {shown?.fetchError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    F2Pool could not be reached, so this is the data saved
                    {shown.fetchedAt ? ` when it was last fetched, ${dateTimeOf(shown.fetchedAt)}` : ' (nothing has been fetched yet)'}.
                    {' '}{shown.fetchError}
                </Alert>
            )}

            {!shown ? (
                loading ? <TableLoader /> : null
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 3 }, mb: 2 }}>
                        {totals.map(([currency, total]) => (
                            <Typography key={currency} variant="body2">
                                Last {HISTORY_DAYS} days ({currency || '-'}): rewards{' '}
                                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'success.main' }}>
                                    {coinAmount(total.rewards)}
                                </Box>
                                {' · '}payouts{' '}
                                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'info.main' }}>
                                    {coinAmount(total.payouts)}
                                </Box>
                            </Typography>
                        ))}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, ml: { md: 'auto' } }}>
                            <LegendItem color={toneOf(theme, 'success').solid} label="Reward (hover for hash rate)" />
                            <LegendItem color={toneOf(theme, 'info').solid} label="Payout (hover for transaction ID)" />
                            <LegendItem color={toneOf(theme, 'error').solid}
                                label={`Paid to another address${mismatches ? ` (${mismatches})` : ''}`} />
                        </Box>
                    </Box>

                    <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: 'minmax(0, 1fr) 96px minmax(0, 1fr)', columnGap: 2, mb: 0.5 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ textAlign: 'right' }}>
                            Rewards · credited by F2Pool
                        </Typography>
                        <span />
                        <Typography variant="overline" color="text.secondary">Payouts · sent on-chain</Typography>
                    </Box>

                    {rows.map(row => (
                        <Box
                            key={row.date}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '72px minmax(0, 1fr)', md: 'minmax(0, 1fr) 96px minmax(0, 1fr)' },
                                gridTemplateAreas: { xs: '"node rewards" "node payouts"', md: '"rewards node payouts"' },
                                columnGap: 2,
                            }}
                        >
                            <Box sx={{ gridArea: 'rewards', display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1, py: 1 }}>
                                {row.rewards.map(entry => <EntryCard key={entry.id} entry={entry} wallet={shown.wallet} />)}
                            </Box>
                            <Box sx={{ gridArea: 'node', position: 'relative', display: 'flex', justifyContent: 'center', minHeight: 72 }}>
                                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 2, transform: 'translateX(-1px)', bgcolor: 'divider' }} />
                                <DateNode date={row.date} active={row.rewards.length + row.payouts.length > 0} />
                            </Box>
                            <Box sx={{ gridArea: 'payouts', display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-start' }, gap: 1, py: 1 }}>
                                {row.payouts.map(entry => <EntryCard key={entry.id} entry={entry} wallet={shown.wallet} />)}
                            </Box>
                        </Box>
                    ))}
                </>
            )}
        </Box>
    );
}
